import "./style.css";
import { useRef, useState, useEffect } from "react";
import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  customerRegister,
  accountEligibility,
  resendEmailAccountVerification
} from "../../../actions/auth";
import { withAlert, types } from "react-alert";
import { useLoading } from "../../../components/LoadingContext";
import {
  AddIcon,
  RoundedChecklistIcon,
  CloseIcon,
  EmailIcon,
  LockIcon,
} from "../../../components/icons";
import { getLocationByAddress } from "../../../actions/googleAPI";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, ScrollProgressBar, fadeUp } from "../../../components/motion";
import {
  POSTAL_CODE_STATUS,
  PostalCodeStatusIcon,
  PostalCodeStatusMessage,
  usePostalCodeLookup,
} from "../../../components/input/postal-code";
import InputForm from "../../../components/input/input-register/InputForm";
import PhoneNumberForm from "../../../components/input/input-register/PhoneNumberForm";
import SelectForm from "../../../components/input/input-register/SelectForm";
import { validateForm } from "./validation/validator";
import FlywireModalTokenization from "../../../components/modal/FlywireModalTokenization";
import { translate } from "../../../actions/googleAPI";
import { optionsPronounce } from "../../../utils/dataLists";
import {
  LOGIN_SUCCESS,
} from "../../../actions/types";
import { pushEventGTM } from "../../../utils/gtm";
import ReactTooltip from "react-tooltip";
import RegisterStepper from "./_components/RegisterStepper";
import FormSection from "./_components/FormSection";
import { RegisterButton, NextArrow, BackArrow } from "./_components/RegisterButtons";

const visa_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/icons/visa.svg";
const master_card_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/icons/master-card.svg";
const jcb_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/icons/jcb.svg";
const american_ex_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/icons/american_ex.svg";

// Step 1 <-> step 2 <-> success: the old view fades out before the new one rises in.
const viewSwap = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

// Saved cards slide in when added and fold away when deleted.
const paymentMethodRowMotion = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto", transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, height: 0, transition: { duration: 0.2 } },
};

const CustomerRegisterPage = ({ alert }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = i18n.language?.toUpperCase();
  const { showLoading, closeLoading } = useLoading();
  const location = useLocation();
  const formData = location.state.formData;

  const contentRef = useRef(null);
  const formRefs = {
    emailRef: useRef(),
    passwordRef: useRef(),
    confirmPasswordRef: useRef(),
    billingPostalCodeRef: useRef(),
    billingCountryRef: useRef(),
    billingNameRef: useRef(),
    billingPhoneNumberRef: useRef(),
    billingAddressRef: useRef(),
    agreeToTermsRef: useRef(),
  }

  const firstErrorRef = useRef(null);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isRegister, setisRegister] = useState(false);
  const [registeredId, setRegisteredId] = useState(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [step, setStep] = useState(1);
  const [profileData, setProfileData] = useState({
    //SECTION 1 - Account Informatino
    email: "",
    password: "",
    confirmPassword: "",

    // SECTION 2 - Billing Information
    billingPostalCode: "",
    billingCity: "",
    billingName: "",
    billingPrefixNumber: "+81",
    billingCountryCode: "JP",
    billingCountry: "Japan",
    billingPhoneNumber: "",
    billingAddress: "",
    billingHonorifics: "Mr.",

    //SECTION 3 (Terms And Condition)
    tnc: false,

    accessTokenGoogle: null,
    avatar: null,
  });
  const [errorsProfileData, setErrorsProfileData] = useState({});
  const [isPostalCodeDisabled, setIsPostalCodeDisabled] = useState(false);
  const [isCityDisabled, setIsCityDisabled] = useState(true);

  useEffect(() => {
    setProfileData({
      ...profileData,
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.password_confirmation,
      accessTokenGoogle: formData.access_token_google ?? null,
      avatar: formData.avatar
    })
  }, []);

  useEffect(() => {
    const contentDiv = contentRef.current;

    function handleScroll() {
      // Check if the user has scrolled to the bottom
      if (
        contentDiv.scrollTop + contentDiv.clientHeight >=
        contentDiv.scrollHeight - 10
      ) {
        setIsScrolledToBottom(true);
      }
    }

    contentDiv.addEventListener("scroll", handleScroll);

    // Cleanup function
    return () => {
      contentDiv.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step])


  // Billing postal code: checked against the geocoding endpoint as the user
  // types (spinner + "Checking postal code…" while it runs). Functional
  // updates, so edits made to other fields during the check are kept.
  const postalCodeLookup = usePostalCodeLookup({
    postalCode: profileData.billingPostalCode,
    countryCode: profileData.billingCountryCode,
    // Countries without postal codes store "-" in a disabled field: nothing to check.
    isEnabled: !isPostalCodeDisabled,
    onFound: (city) => {
      setErrorsProfileData((previousErrors) => ({ ...previousErrors, billingPostalCode: null }));
      setProfileData((previousData) => ({ ...previousData, billingCity: city }));
    },
    onNotFound: () => {
      setErrorsProfileData((previousErrors) => ({
        ...previousErrors,
        billingPostalCode: "individualRegister.validation.billingPostalCodeInvalid",
      }));
      setProfileData((previousData) => ({ ...previousData, billingCity: "" }));
      formRefs.billingPostalCodeRef.current?.scrollIntoView({ block: "center" });
    },
  });
  const isCheckingPostalCode = postalCodeLookup.status === POSTAL_CODE_STATUS.CHECKING;

  // Function to handle the checkbox change
  const handleAgreeToTermsChange = (e) => {
    setProfileData({
      ...profileData,
      tnc: e.target.checked
    })
  };

  const handleChangeProfile = (e) => {
    const { name, value } = e.target;
    if (name === "billingName" || name === "billingAddress") {
      const filteredValue = value.replace(/[^\w\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/g, "")
      setProfileData({
        ...profileData,
        [name]: filteredValue,
      });
    } else {
      setProfileData({
        ...profileData,
        [name]: value,
      });
    }

    setErrorsProfileData({
      ...errorsProfileData,
      [name]: null
    })
  };

  const handleLiClickCountryList3 = (dialCode, countryCode, name) => {
    const countryWithoutPostCode = process.env.REACT_APP_COUNTRY_WITHOUT_POSTCODE;

    if (!countryWithoutPostCode.split(',').includes(countryCode)) {
      setIsPostalCodeDisabled(false);
      setIsCityDisabled(true);
      setProfileData({
        ...profileData,
        billingPostalCode: "",
        billingCity: "",
        billingPrefixNumber: dialCode,
        billingCountryCode: countryCode,
        billingCountry: name,
      })
    } else {
      setProfileData({
        ...profileData,
        billingPostalCode: "-",
        billingCity: "",
        billingPrefixNumber: dialCode,
        billingCountryCode: countryCode,
        billingCountry: name,
      })
      setIsPostalCodeDisabled(true);
      setIsCityDisabled(false);
    }
  };

  const validateProfileForm = async () => {
    const { validationSuccess, firstError, validationMessage } = await validateForm(profileData);

    firstErrorRef.current = null;
    if (!validationSuccess) {
      setErrorsProfileData(validationMessage);

      firstErrorRef.current = formRefs[firstError + "Ref"].current;

      if (firstErrorRef.current) {
        firstErrorRef.current.scrollIntoView({ block: "center" });
      }
    }

    return validationSuccess;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validateForm = await validateProfileForm();

    if (validateForm) {
      if (step === 1) {
        setStep(2);
      } else {
        if (paymentMethods.length < 1) {
          const checkEligibility = await accountEligibility(profileData.email, "individual");

          if (checkEligibility.eligibilityStatus) {
            setStep(2);
          } else {
            alert.show(t("The email has been used."), {
              type: types.ERROR,
            });
          }
          alert.show(t("Please add at least one payment method"), {
            type: types.ERROR,
          });
        } else {
          showLoading(true);
          const billingInformation = {
            name: profileData.billingName,
            phone: profileData.billingPhoneNumber,
            address: profileData.billingAddress,
            postal_code: profileData.billingPostalCode,
            country: profileData.billingCountry,
            pronounced: profileData.billingHonorifics,
            prefix_number: profileData.billingPrefixNumber,
            country_code: profileData.billingCountryCode,
            city: profileData.billingCity,
          };

          const registerResponse = await customerRegister({
            name: profileData.billingName,
            email: profileData.email,
            password: profileData.password,
            confirmPassword: profileData.confirmPassword,
            billingInformation: billingInformation,
            paymentMethod: paymentMethods,
            googleSignUpToken: profileData.accessTokenGoogle,
            avatar: profileData.avatar,
            lang: lang,
          });

          if (registerResponse.data.code === 201 && registerResponse.data.desc === "SUCCESS") {
            closeLoading(true);
            pushEventGTM({
              event: 'sign_up',
              method: registerResponse.data.data.withGoogle ? 'Google' : 'Email',
            });
            if (registerResponse.data.data.withGoogle) {
              dispatch({
                type: LOGIN_SUCCESS,
                payload: { token: registerResponse.data.data.token },
              });
              history.push({
                pathname: "/",
              });
            } else {
              setisRegister(true);
              setRegisteredId(registerResponse.data.data.registeredId)
              window.scrollTo(0, 0);
            }
          } else {
            closeLoading(true);
            alert.show(t("Registration failed"), { type: types.ERROR });
          }
        }
      }
    } else {
      // alert.show(
      //   t("Please fill in all the required fields (highlighted in red)."),
      //   { type: types.ERROR }
      // );
    }
  };

  const resendEmailHandler = async () => {
    const resendEmail = await resendEmailAccountVerification({
      registeredId: registeredId,
    })

    if (resendEmail.status === 200) {
      alert.show("Resend Email Success", { type: types.SUCCESS });
    } else {
      alert.show(t("Re-send Email Failed"), { type: types.ERROR });
    }
  }

  const extractFirstAndLastName = async (fullName) => {
    // Split the full name into an array of strings
    const nameParts = fullName.trim().split(" ");

    // Extract the first name (first element of the array)
    const firstName = nameParts[0];

    // Extract the last name (last element of the array, if exists)
    const lastName =
      nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";

    // Return an object containing the first name and last name
    return {
      firstName: firstName,
      lastName: lastName ? lastName : firstName,
    };
  };

  const openModalFlywire = async () => {
    showLoading(true);
    const countryWithoutPostCode = process.env.REACT_APP_COUNTRY_WITHOUT_POSTCODE;

    let city;
    let zipCode;
    if (countryWithoutPostCode.split(',').includes(profileData.billingCountryCode)) {
      city = profileData.billingCity;
      zipCode = "-";
    } else {
      const zipCodeCheck = await getLocationByAddress(profileData.billingPostalCode, `${profileData.billingCountryCode}`);
      if (zipCodeCheck.address_components.filter((item) => item.types.includes("administrative_area_level_2") || item.types.includes("locality")).length !== 0) {
        city = zipCodeCheck.address_components.filter((item) => item.types.includes("administrative_area_level_2") || item.types.includes("locality"))[0].long_name;
      } else {
        city = zipCodeCheck.address_components[zipCodeCheck.address_components.length - 2].long_name;
      }

      zipCode = profileData.billingPostalCode;
    }
    const { firstName, lastName } = await extractFirstAndLastName(profileData.billingName);

    const regex = /[\u4E00-\u9FFF\u3040-\u30FF\uFF66-\uFF9F]/
    const checkAddressContainsJapanese = regex.test(profileData.billingAddress);
    let finalAddress
    if (checkAddressContainsJapanese) {
      const translateResult = await translate("en", profileData.billingAddress)

      finalAddress = translateResult.translatedText.replace("〒", "");
    } else {
      finalAddress = profileData.billingAddress;
    }

    closeLoading(true);

    FlywireModalTokenization({
      firstName: firstName,
      lastName: lastName,
      email: profileData.email,
      phone: `${profileData.billingPrefixNumber} ${profileData.billingPhoneNumber}`,
      address: finalAddress,
      city: city,
      zip: zipCode,
      country: profileData.billingCountryCode,
      locale: lang === "EN" ? "en" : "ja",
      successCallback: (result) => {
        const newPaymentMethod = {
          method: "card",
          cardNumber: result.digits,
          mmyy: `${result.expirationMonth.padStart(2, "0")}${result.expirationYear.substr(-2)}`,
          cvv: "cvv",
          name: "cardholderName",
          country: "countryOrRegion",
          isActive: paymentMethods.length > 0 ? false : true,
          token: result.token,
          type: result.type,
          paymentMethod: result.paymentMethod,
          brand: result.brand,
          expirationMonth: result.expirationMonth,
          expirationYear: result.expirationYear,
        };

        setPaymentMethods((prevPaymentMethods) => [
          ...prevPaymentMethods,
          newPaymentMethod,
        ]);
      }
    });
  };

  const handlePaymentMethodActivation = (index) => {
    const updatedPaymentMethods = paymentMethods.map((method, i) => ({
      ...method,
      isActive: i === index ? true : false,
    }));
    // Assuming you're using useState to manage paymentMethods
    // Update the state with the modified array
    setPaymentMethods(updatedPaymentMethods);
  };

  function getCardTypeByBrand(type) {
    if (type === "VISA") {
      return visa_icon;
    } else if (type === "MASTERCARD") {
      return master_card_icon;
    } else if (type === "AMERICAN EXPRESS") {
      return american_ex_icon;
    } else if (type === "JCB") {
      return jcb_icon;
    }

    return "Unknown"; // If none of the patterns match
  }

  const deletePaymentMethod = (index) => {
    const confirmDelete = window.confirm(t("registration.delete_confirmation"));
    if (confirmDelete) {
      const updatedPaymentMethods = paymentMethods.filter(
        (_, i) => i !== index
      );
      setPaymentMethods(updatedPaymentMethods);
    }
  };

  const stepLabels = [
    t("individualRegister.personalInformationDesc"),
    t("individualRegister.paymentMethodDesc"),
  ];
  const hasPaymentMethod = paymentMethods.length > 0;

  return (
    <FrontLayout>
      <div className="bg-page-new">
        <div className="tw:mx-auto tw:w-full tw:max-w-[960px] tw:px-4 tw:pt-10 tw:pb-16 tw:md:pt-14 tw:md:pb-20">
          <AnimatePresence mode="wait" initial={false}>
            {!isRegister ? (
              <motion.div
                key="form"
                variants={viewSwap}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Page header: title + 2-step progress */}
                <div className="tw:mb-8">
                  <Reveal
                    as="h4"
                    onMount
                    y={16}
                    className="tw:!m-0 tw:!font-libre-caslon-display tw:!text-[28px] tw:md:!text-[34px] tw:!font-normal tw:!text-[#282828]"
                  >
                    {t("individualRegister.title")}
                  </Reveal>
                  <div className="tw:mt-6">
                    <RegisterStepper steps={stepLabels} currentStep={step} />
                  </div>
                </div>

                {/* Form card */}
                <motion.div
                  className="tw:bg-white tw:rounded-2xl tw:border tw:border-[#EFEFEF] tw:shadow-[0_8px_30px_rgba(0,0,0,0.04)] tw:p-5 tw:sm:p-8 tw:md:p-10"
                  variants={fadeUp(24)}
                  initial="hidden"
                  animate="visible"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {step === 1 ? (
                      <motion.div
                        key="step-1"
                        variants={viewSwap}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                      >
                        <FormSection title={t("individualRegister.accountInformation.title")}>
                          <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-2">
                            <div className="tw:md:col-span-2">
                              <InputForm
                                innerRef={formRefs.emailRef}
                                type={"text"}
                                suffixClass={"mb-0 fs-14"}
                                label={t("individualRegister.accountInformation.form.email")}
                                placeholder={t("individualRegister.accountInformation.form.email")}
                                prefixIcon={<EmailIcon />}
                                id={"emailAddress"}
                                name={"email"}
                                value={profileData.email}
                                onChange={handleChangeProfile}
                                isInvalid={errorsProfileData.email}
                                invalidMessage={t(errorsProfileData.email)}
                              />
                            </div>
                            <InputForm
                              innerRef={formRefs.passwordRef}
                              type={"password"}
                              suffixClass={"mb-0 fs-14"}
                              label={t("individualRegister.accountInformation.form.password")}
                              placeholder={t("individualRegister.accountInformation.form.password")}
                              prefixIcon={<LockIcon />}
                              id={"password"}
                              name={"password"}
                              value={profileData.password}
                              onChange={handleChangeProfile}
                              isInvalid={errorsProfileData.password}
                              invalidMessage={t(errorsProfileData.password)}
                            />
                            <InputForm
                              innerRef={formRefs.confirmPasswordRef}
                              type={"password"}
                              suffixClass={"mb-0 fs-14"}
                              label={t("individualRegister.accountInformation.form.passwordConfirmation")}
                              placeholder={t("individualRegister.accountInformation.form.passwordConfirmation")}
                              prefixIcon={<LockIcon />}
                              id={"confirmPassword"}
                              name={"confirmPassword"}
                              value={profileData.confirmPassword}
                              onChange={handleChangeProfile}
                              isInvalid={errorsProfileData.confirmPassword}
                              invalidMessage={t(errorsProfileData.confirmPassword)}
                            />
                          </div>
                        </FormSection>

                        <FormSection
                          title={t("individualRegister.billingInformation.title")}
                          description={t("individualRegister.billingInformation.desc")}
                        >
                          {/* Honorific + name share a row on phones; phone number wraps below. */}
                          <div className="tw:grid tw:grid-cols-3 tw:gap-4 tw:md:grid-cols-12">
                            <div className="tw:col-span-1 tw:md:col-span-2">
                              <SelectForm
                                name={"billingHonorifics"}
                                suffixClass={"mb-0 fs-14"}
                                isSearchable={false}
                                options={optionsPronounce}
                                defaultValue={optionsPronounce[0]}
                                value={setProfileData.billingHonorifics}
                                onChange={(e) => setProfileData({
                                  ...profileData,
                                  billingHonorifics: e.value
                                })}
                                label={t("individualRegister.billingInformation.form.honorifics")}
                                placeholder={t("individualRegister.billingInformation.form.honorifics")}
                              />
                            </div>
                            <div className="tw:col-span-2 tw:md:col-span-5">
                              <InputForm
                                innerRef={formRefs.billingNameRef}
                                type={"text"}
                                suffixClass={"mb-0 fs-14"}
                                label={t("individualRegister.billingInformation.form.name")}
                                placeholder={t("individualRegister.billingInformation.form.name")}
                                id={"billingName"}
                                name={"billingName"}
                                value={profileData.billingName}
                                onChange={handleChangeProfile}
                                isInvalid={errorsProfileData.billingName}
                                invalidMessage={t(errorsProfileData.billingName)}
                              />
                            </div>
                            <div className="tw:col-span-3 tw:md:col-span-5">
                              <PhoneNumberForm
                                innerRef={formRefs.billingPhoneNumberRef}
                                id="billingPhoneNumber"
                                suffixClass={"mb-0 fs-14"}
                                name="billingPhoneNumber"
                                label={t("individualRegister.billingInformation.form.phoneNumber")}
                                placeholder={t("individualRegister.billingInformation.form.phoneNumber")}
                                countryCode={profileData.billingCountryCode}
                                prefixPhoneNumber={profileData.billingPrefixNumber}
                                value={profileData.billingPhoneNumber}
                                onClickCountryList={handleLiClickCountryList3}
                                onChange={handleChangeProfile}
                                isInvalid={errorsProfileData.billingPhoneNumber}
                                invalidMessage={t(errorsProfileData.billingPhoneNumber)}
                              />
                            </div>
                          </div>

                          <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:mt-4 tw:md:grid-cols-3">
                            <InputForm
                              innerRef={formRefs.billingCountryRef}
                              type={"text"}
                              suffixClass={"mb-0 fs-14"}
                              label={t("individualRegister.billingInformation.form.country")}
                              placeholder={t("individualRegister.billingInformation.form.country")}
                              id={"billingCountry"}
                              name={"billingCountry"}
                              value={profileData.billingCountry}
                              onChange={handleChangeProfile}
                              isInvalid={errorsProfileData.billingCountry}
                              invalidMessage={t(errorsProfileData.billingCountry)}
                              readOnly={true}
                              disable={true}
                            />
                            <div>
                              <InputForm
                                innerRef={formRefs.billingPostalCodeRef}
                                type={"text"}
                                suffixClass={"mb-0 fs-14"}
                                label={t("individualRegister.billingInformation.form.postalCode")}
                                placeholder={t("individualRegister.billingInformation.form.postalCode")}
                                id={"billingPostalCode"}
                                name={"billingPostalCode"}
                                value={profileData.billingPostalCode}
                                onChange={handleChangeProfile}
                                disable={isPostalCodeDisabled}
                                readOnly={isPostalCodeDisabled}
                                isInvalid={errorsProfileData.billingPostalCode}
                                invalidMessage={t(errorsProfileData.billingPostalCode)}
                                suffixIcon={<PostalCodeStatusIcon status={postalCodeLookup.status} />}
                              />
                              <PostalCodeStatusMessage status={postalCodeLookup.status} onRetry={postalCodeLookup.retry} />
                            </div>
                            <InputForm
                              type={"text"}
                              suffixClass={"mb-0 fs-14"}
                              label={t("individualRegister.billingInformation.form.city")}
                              placeholder={
                                isCheckingPostalCode
                                  ? t("PostalCodeStatus.LookingUpCity")
                                  : t("individualRegister.billingInformation.form.city")
                              }
                              id={"billingCity"}
                              name={"billingCity"}
                              value={profileData.billingCity}
                              onChange={handleChangeProfile}
                              isInvalid={errorsProfileData.billingCity}
                              invalidMessage={t(errorsProfileData.billingCity)}
                              readOnly={isCityDisabled}
                              disable={isCityDisabled}
                            />
                          </div>

                          <div className="tw:mt-4">
                            <InputForm
                              innerRef={formRefs.billingAddressRef}
                              type={"text"}
                              suffixClass={"mb-0 fs-14"}
                              label={t("individualRegister.billingInformation.form.address")}
                              placeholder={t("individualRegister.billingInformation.form.address")}
                              id={"billingAddress"}
                              name={"billingAddress"}
                              value={profileData.billingAddress}
                              onChange={handleChangeProfile}
                              isInvalid={errorsProfileData.billingAddress}
                              invalidMessage={t(errorsProfileData.billingAddress)}
                            />
                          </div>
                        </FormSection>

                        <FormSection title={t("individualRegister.termsAndCondition.title")} isLast>
                          <div className="tw:relative tw:overflow-hidden tw:rounded-xl tw:border tw:border-[#F1E3BF] tw:bg-[#FFF8E8] tw:p-5 tw:md:p-6">
                            {/* Fills as the terms are read; the checkbox unlocks at the end. */}
                            <ScrollProgressBar containerRef={contentRef} />
                            <div
                              className="tw:overflow-auto custom-scrollbar tw:h-[320px] tw:pr-2"
                              ref={contentRef}
                              dangerouslySetInnerHTML={{
                                __html: `${t("termConditionContent")}`,
                              }}
                            ></div>
                          </div>

                          <div className="tw:mt-4" data-tip data-for="tncTooltip">
                            <label
                              htmlFor="tnc"
                              className={`tw:inline-flex! tw:items-start tw:gap-3 tw:m-0 tw:text-[14px] tw:text-[#282828] ${isScrolledToBottom ? "tw:cursor-pointer" : "tw:cursor-not-allowed tw:opacity-60"}`}
                            >
                              <input
                                ref={formRefs.agreeToTermsRef}
                                type="checkbox"
                                id="tnc"
                                className="tw:mt-[2px] tw:w-4 tw:h-4 tw:shrink-0 tw:accent-[#DBBE78]"
                                checked={profileData.tnc}
                                onChange={handleAgreeToTermsChange}
                                disabled={!isScrolledToBottom}
                              />
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: `${t("individualRegister.termsAndCondition.checkbox")}`,
                                }}
                              ></span>
                            </label>
                            <ReactTooltip
                              id="tncTooltip"
                              backgroundColor="#4A4A4A"
                              textColor="#FFFFFF"
                            >
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: t("NewCheckout.TncTooltip"),
                                }}
                              ></span>
                            </ReactTooltip>
                            {errorsProfileData.agreeToTerms && (
                              <div className="tw:mt-1 tw:text-[13px] tw:text-[#dc3545]">
                                {errorsProfileData.agreeToTerms}
                              </div>
                            )}
                          </div>
                        </FormSection>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="step-2"
                        variants={viewSwap}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                      >
                        <FormSection
                          title={t("individualRegister.paymentMethod.title")}
                          description={t("individualRegister.paymentMethod.desc")}
                          isLast
                        >
                          {/* Saved cards: the selected one is outlined in gold. */}
                          <AnimatePresence initial={false}>
                            {paymentMethods.map((item, index) => (
                              <motion.div
                                // Keyed by the card itself (not the index) so deleting a card animates that card away.
                                key={`${item.brand}-${item.cardNumber}-${item.mmyy}`}
                                {...paymentMethodRowMotion}
                                className="tw:overflow-hidden"
                              >
                                <div className="tw:pb-3">
                                  <div
                                    className={`tw:flex tw:items-center tw:gap-2 tw:rounded-xl tw:border tw:pr-2 tw:transition-colors tw:duration-200 ${item.isActive
                                      ? "tw:border-[#DBBE78] tw:bg-[#FFFBF2]"
                                      : "tw:border-[#E5E5E5] tw:bg-white tw:hover:border-[#C9C9C9]"
                                      }`}
                                  >
                                    <label
                                      htmlFor={`paymentMethod${index}`}
                                      className="tw:flex! tw:flex-1 tw:min-w-0 tw:items-center tw:gap-4 tw:m-0 tw:px-4 tw:py-4 tw:cursor-pointer"
                                    >
                                      <input
                                        className="tw:w-4 tw:h-4 tw:shrink-0 tw:accent-[#DBBE78]"
                                        type="radio"
                                        name="paymentMethod"
                                        checked={item.isActive}
                                        id={`paymentMethod${index}`}
                                        onChange={() => handlePaymentMethodActivation(index)}
                                      />
                                      <img src={getCardTypeByBrand(item.brand)} alt={item.brand} className="tw:h-6 tw:w-auto" />
                                      <span className="tw:flex tw:flex-col tw:min-w-0">
                                        <span className="tw:text-[15px] tw:font-semibold tw:tracking-wider tw:text-[#282828]">
                                          {"•••• " + item.cardNumber}
                                        </span>
                                        <span className="tw:text-[13px] tw:text-[#8D8D8D]">
                                          {`Exp. ${item.mmyy.slice(0, 2)}/${item.mmyy.slice(2)}`}
                                        </span>
                                      </span>
                                    </label>
                                    <button
                                      type="button"
                                      className="tw:flex tw:items-center tw:justify-center tw:w-9 tw:h-9 tw:shrink-0 tw:rounded-full! tw:bg-transparent tw:border-0 tw:cursor-pointer tw:transition-colors tw:hover:bg-[#F3F3F3]"
                                      aria-label="Remove card"
                                      onClick={() => deletePaymentMethod(index)}
                                    >
                                      <CloseIcon size={12} />
                                    </button>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </AnimatePresence>

                          {hasPaymentMethod ? (
                            <RegisterButton
                              variant="outline"
                              icon={<AddIcon color="#282828" />}
                              iconFirst
                              className="tw:w-full tw:sm:w-auto tw:mt-1"
                              onClick={openModalFlywire}
                            >
                              {t("individualRegister.paymentMethod.button")}
                            </RegisterButton>
                          ) : (
                            // Empty state: nothing saved yet, so the add button is the focus.
                            <div className="tw:flex tw:flex-col tw:items-center tw:gap-5 tw:rounded-xl tw:border tw:border-dashed tw:border-[#D9D9D9] tw:bg-[#FAFAFA] tw:px-6 tw:py-10 tw:text-center">
                              <div className="tw:flex tw:items-center tw:gap-3">
                                {[visa_icon, master_card_icon, jcb_icon, american_ex_icon].map((icon) => (
                                  <img key={icon} src={icon} alt="" className="tw:h-6 tw:w-auto" />
                                ))}
                              </div>
                              <RegisterButton
                                variant="dark"
                                icon={<AddIcon color="#FFFFFF" />}
                                iconFirst
                                className="tw:w-full tw:sm:w-auto"
                                onClick={openModalFlywire}
                              >
                                {t("individualRegister.paymentMethod.button")}
                              </RegisterButton>
                            </div>
                          )}
                        </FormSection>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Step actions: Back on the left (step 2 only), the main action on the right. */}
                <div className="tw:mt-8 tw:flex tw:flex-col-reverse tw:gap-3 tw:sm:flex-row tw:sm:items-center tw:sm:justify-between">
                  {step === 2 ? (
                    <RegisterButton
                      variant="outline"
                      icon={<BackArrow />}
                      iconFirst
                      className="tw:w-full tw:sm:w-auto"
                      onClick={() => setStep(1)}
                    >
                      {t("individualRegister.back")}
                    </RegisterButton>
                  ) : (
                    <span className="tw:hidden tw:sm:block" />
                  )}
                  <RegisterButton
                    icon={<NextArrow />}
                    className="tw:w-full tw:sm:w-auto tw:sm:min-w-[260px]"
                    disabled={step === 2 && !hasPaymentMethod}
                    onClick={handleSubmit}
                  >
                    {step === 1 ? t("individualRegister.proceedNextStep") : t("individualRegister.submitButton")}
                  </RegisterButton>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                className="tw:flex tw:flex-col tw:items-center tw:text-center tw:bg-white tw:rounded-2xl tw:border tw:border-[#EFEFEF] tw:shadow-[0_8px_30px_rgba(0,0,0,0.04)] tw:px-5 tw:py-10 tw:sm:px-10 tw:md:py-14"
                variants={viewSwap}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <motion.div
                  className="tw:mb-6"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 380, damping: 18, delay: 0.15 }}
                >
                  <RoundedChecklistIcon color="#3CAEA3" size={46} />
                </motion.div>
                <h3 className="tw:!m-0 tw:!mb-4 tw:!font-libre-caslon-display tw:!text-[26px] tw:sm:!text-[30px] tw:!font-normal tw:!text-[#282828]">
                  {t("individualRegister.successPage.title.1")}
                </h3>
                <div
                  className="tw:max-w-[560px] tw:text-[14px] tw:leading-relaxed tw:text-[#282828]"
                  dangerouslySetInnerHTML={{
                    __html: t("individualRegister.successPage.desc.1", { email: profileData.email }),
                  }}
                />

                {/* Resend verification email */}
                <div className="tw:w-full tw:max-w-[420px] tw:mt-8 tw:rounded-xl tw:bg-[#F3F3F3] tw:px-6 tw:py-6">
                  <div
                    className="tw:mb-4 tw:text-[14px] tw:text-[#282828] tw:break-words"
                    dangerouslySetInnerHTML={{
                      __html: t("individualRegister.successPage.card.desc"),
                    }}
                  />
                  <RegisterButton variant="dark" className="tw:w-full" onClick={resendEmailHandler}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t("individualRegister.successPage.card.button"),
                      }}
                    />
                  </RegisterButton>
                </div>

                <div
                  className="tw:mt-8 tw:max-w-[560px] tw:text-[14px] tw:leading-relaxed tw:text-[#282828]"
                  dangerouslySetInnerHTML={{
                    __html: t("individualRegister.successPage.desc.4"),
                  }}
                />
                <RegisterButton
                  className="tw:w-full tw:sm:w-auto tw:sm:min-w-[260px] tw:mt-8"
                  onClick={() => history.push("/")}
                >
                  {t("individualRegister.successPage.button")}
                </RegisterButton>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </FrontLayout>
  );
};

export default withAlert()(CustomerRegisterPage);
